import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Breadcrumb from '../../../components/Breadcrumb';
import Page from '../../../components/Page';
import Pagination from '../../../components/Pagination';
import client from '../../../lib/feathers';

const STATUS_LIST = ['draft', 'scheduled', 'sending', 'sent', 'cancelled'];

const TYPE_LIST = [
  { value: 'claim_listing', label: 'Claim Listing' },
  { value: 'access_pass', label: 'Access Pass' },
  { value: 'staff_access', label: 'Staff Access' },
  { value: 'upgrade', label: 'Upgrade' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'custom', label: 'Custom' },
];

const statusBadgeStyles = {
  draft: 'bg-gray-100 text-gray-800',
  scheduled: 'bg-yellow-100 text-yellow-800',
  sending: 'bg-blue-100 text-blue-800',
  sent: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const getTypeLabel = (value) => {
  const match = TYPE_LIST.find((t) => t.value === value);
  return match ? match.label : value;
};

const calcOpenRate = (stats) => {
  if (!stats || !stats.delivered || stats.delivered === 0) return null;
  const rate = ((stats.opened || 0) / stats.delivered) * 100;
  return `${rate.toFixed(1)}%`;
};

export default function OutreachCampaigns() {
  const navigate = useNavigate();
  const [query] = useSearchParams();
  const limit = 25;

  const [page, setPage] = useState(Number(query.get('page')) || 0);
  const [statusFilter, setStatusFilter] = useState(query.get('status') || undefined);
  const [typeFilter, setTypeFilter] = useState(query.get('type') || '');

  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getCampaigns = async () => {
    setLoading(true);
    setError(null);
    try {
      const q = {
        $limit: limit,
        $skip: page * limit,
        $sort: { createdAt: -1 },
      };

      if (statusFilter) {
        q.status = statusFilter;
      }

      if (typeFilter) {
        q.type = typeFilter;
      }

      const result = await client.service('admin/outreach-campaign').find({ query: q });
      setCampaigns(result.data);
      setTotal(result.total);
    } catch (e) {
      console.error('Error loading campaigns:', e);
      setError('There was a problem loading campaigns. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const navigateToPage = (p = 0) => {
    if (p * limit > total || p * limit < 0) return;
    navigate(`/admin/outreach/campaigns?page=${p}`);
    setPage(p);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status || undefined);
    setPage(0);
  };

  const handleTypeFilter = (e) => {
    setTypeFilter(e.target.value);
    setPage(0);
  };

  useEffect(() => {
    getCampaigns();
  }, [page, statusFilter, typeFilter]);

  return (
    <Page title="Outreach Campaigns">

      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <nav className="flex" aria-label="Breadcrumb">
          <Breadcrumb
            elements={[
              { name: 'Outreach', href: '/admin/outreach' },
              { name: 'Campaigns' },
            ]}
          />
        </nav>
        <div className="mt-3 sm:mt-0 sm:ml-4">
          <Link
            to="/admin/outreach/campaigns/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700"
          >
            New Campaign
          </Link>
        </div>
      </div>

      <div className="pb-4 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Campaigns ({total} Total)
        </h3>
        <div className="mt-3 sm:mt-0">
          <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
        </div>
      </div>

      <div className="py-3 flex flex-col sm:flex-row sm:items-end gap-4">
        <div className="flex-1">
          <div className="sm:hidden">
            <label htmlFor="status-select" className="sr-only">Filter by status</label>
            <select
              id="status-select"
              value={statusFilter || ''}
              onChange={(e) => handleStatusFilter(e.target.value)}
              className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-gray-500 focus:border-gray-500 sm:text-sm rounded-md"
            >
              <option value="">All Statuses</option>
              {STATUS_LIST.map((s) => (
                <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>
          <div className="hidden sm:block">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-6" aria-label="Status filter">
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); handleStatusFilter(undefined); }}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${!statusFilter ? 'border-gray-500 text-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                >
                  All
                </a>
                {STATUS_LIST.map((s) => (
                  <a
                    key={s}
                    href="#"
                    onClick={(e) => { e.preventDefault(); handleStatusFilter(s); }}
                    className={`capitalize whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${statusFilter === s ? 'border-gray-500 text-gray-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                  >
                    {s}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </div>

        <div className="sm:w-48">
          <label htmlFor="type-select" className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
            Type
          </label>
          <select
            id="type-select"
            value={typeFilter}
            onChange={handleTypeFilter}
            className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-gray-500 focus:border-gray-500 rounded-md"
          >
            <option value="">All Types</option>
            {TYPE_LIST.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recipients
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Open Rate
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sent Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created By
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">View</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading && (
                    <tr>
                      <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                        Loading campaigns...
                      </td>
                    </tr>
                  )}
                  {!loading && campaigns.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-6 py-10 text-center text-sm text-gray-500">
                        No campaigns found.
                      </td>
                    </tr>
                  )}
                  {!loading && campaigns.map((c) => {
                    const openRate = calcOpenRate(c.stats);
                    return (
                      <tr
                        key={c.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/admin/outreach/campaigns/${c.id}`)}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{c.name}</div>
                          {c.templateId && (
                            <div className="text-xs text-gray-400 mt-0.5">Template: {c.templateId}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-700">{getTypeLabel(c.type)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${statusBadgeStyles[c.status] || 'bg-gray-100 text-gray-800'}`}>
                            {c.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {c.stats ? (c.stats.total || 0).toLocaleString() : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {openRate || <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {c.sentAt
                            ? format(new Date(c.sentAt), 'MMM d, yyyy')
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {c.createdBy
                            ? `${c.createdBy.firstName || ''} ${c.createdBy.lastName || ''}`.trim() || c.createdBy.email
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/admin/outreach/campaigns/${c.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-primary hover:text-red-900"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <Pagination total={total} limit={limit} page={page} navigateToPage={navigateToPage} />
            </div>
          </div>
        </div>
      </div>

    </Page>
  );
}
